import { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchGameAdmins, addGameAdmin, removeGameAdmin } from '../../api/games';

const STEAM_ID_RE = /^7656119[0-9]{10}$/;

interface Props {
  gameId: string;
}

export function AdminsSection({ gameId }: Props) {
  const qc = useQueryClient();
  const [input, setInput] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['game-admins', gameId],
    queryFn: () => fetchGameAdmins(gameId),
  });

  const addMutation = useMutation({
    mutationFn: (steamId: string) => addGameAdmin(gameId, steamId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['game-admins', gameId] });
      setInput('');
      setAddError(null);
    },
    onError: (err: Error) => setAddError(err.message),
  });

  const removeMutation = useMutation({
    mutationFn: (steamId: string) => removeGameAdmin(gameId, steamId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['game-admins', gameId] }),
  });

  const inputInvalid = input.length > 0 && !STEAM_ID_RE.test(input);

  const handleAdd = () => {
    if (!STEAM_ID_RE.test(input)) {
      setAddError('Ungültige Steam64-ID. Format: 7656119XXXXXXXXXX');
      return;
    }
    setAddError(null);
    addMutation.mutate(input);
  };

  const adminIds = data?.admin_steam_ids ?? [];

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
        Admin Steam IDs
      </Typography>

      {isLoading && <CircularProgress size={20} />}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load admins.
        </Alert>
      )}

      {!isLoading && adminIds.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Noch keine Admins. Füge deine Steam64-ID hinzu.
        </Typography>
      )}

      {adminIds.length > 0 && (
        <List dense disablePadding>
          {adminIds.map((steamId) => (
            <ListItem
              key={steamId}
              disableGutters
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Steam-Profil öffnen">
                    <IconButton
                      size="small"
                      component={Link}
                      href={`https://steamcommunity.com/profiles/${steamId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Entfernen">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeMutation.mutate(steamId)}
                      disabled={removeMutation.isPending}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            >
              <ListItemText
                primary={steamId}
                primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace' }}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Divider sx={{ my: 1 }} />

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          label="Steam64-ID hinzufügen"
          placeholder="76561198XXXXXXXXX"
          value={input}
          onChange={(e) => { setInput(e.target.value); setAddError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          error={inputInvalid || addError !== null}
          helperText={
            inputInvalid
              ? 'Format: 7656119XXXXXXXXXX (17 Ziffern)'
              : (addError ?? 'Enter oder +-Button')
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Hinzufügen">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleAdd}
                      disabled={addMutation.isPending || !input}
                    >
                      {addMutation.isPending ? <CircularProgress size={16} /> : <AddIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        Änderungen wirken nach dem nächsten Neustart (ausser Enshrouded).
      </Typography>
    </Box>
  );
}
